import React, { useEffect, useState } from 'react';
import { Avatar, Box, Divider, IconButton, Menu, MenuItem, Stack, Switch, Tooltip, Badge } from '@mui/material'; // Added Tooltip and Badge
import { Gear, Palette, LockKey, Key, SignOut, User, TextAa, CalendarBlank } from "phosphor-react"; // Import TextAa icon and CalendarBlank
import useSettings from '../../hooks/useSettings';
import { faker } from '@faker-js/faker';
import AntSwitch from '../../components/AntSwitch';
import LogoTop from '../../assets/Images/logo-top.png'; // Changed import to LogoTop
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { PATH_DASHBOARD } from '../../routes/paths'; // Import PATH_DASHBOARD
import { useSelector, useDispatch } from 'react-redux'; // Import useSelector and useDispatch
import { Nav_Buttons } from '../../data'; // Re-import Nav_Buttons and Profile_Menu
import { PROFILE_IMAGE_URL } from '../../config'; // Import PROFILE_IMAGE_URL
import createAvatar from '../../utils/createAvatar'; // Import createAvatar
import { ResetMessageBadge, ResetPagerBadge } from '../../redux/slices/app'; // Import reset actions

const getPath = (index) =>{
  switch (index) {
    case 0:
      return '/group'
    
    case 1:
      return '/app'

    case 2: // Pager path
      return '/pager'

    // Removed the call icon entry
    // The next index is now 3 for '/advanced-search'

    case 3: // Shifted from 4 to 3
      return '/advanced-search'

    case 4: // Settings path, shifted from 5 to 4
      return '/settings'
  
    default:
      break;
  }
};

const getMenuPath = (index) =>{
  switch (index) {
    case 0: // Profile
      return '/profile';
      
    case 1: // Logout
      // todo - update token and set isAuth = false
      return '/auth/login';
      
    default:
      break;
  }
}

const resolveIndex = (pathname) => {
  if (pathname.startsWith('/group')) return 0;
  if (pathname.startsWith('/app')) return 1;
  if (pathname.startsWith('/pager')) return 2; // Pager index
  // Removed '/call' index (was 3)
  if (pathname.startsWith('/advanced-search')) return 3; // Shifted from 4 to 3
  if (pathname.startsWith('/settings')) return 4; // Settings index, shifted from 5 to 4
  return 0;
};

const SideBar = () => {
  const dispatch = useDispatch();
  const { messageBadgeCount, pagerBadgeCount } = useSelector((state) => state.app);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const userData = sessionStorage.getItem('currentUser');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
  }, []);

  // Determine avatar source and properties
  const userName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User';
  const avatarSrc = currentUser?.profilePicture 
    ? (currentUser.profilePicture.startsWith('http') 
        ? currentUser.profilePicture 
        : `${PROFILE_IMAGE_URL}${currentUser.callingId}`) 
    : undefined;
  const avatarProps = createAvatar(userName);


  // state for selected button
  const [selected, setSelected] = useState(() => resolveIndex(location.pathname || '/group')); // default to groups

  useEffect(() => {
    const currentIndex = resolveIndex(location.pathname);
    if (currentIndex !== undefined && currentIndex !== selected) {
      setSelected(currentIndex);
    }
  }, [location.pathname, selected]);
  //switch themes
  const {onToggleMode} = useSettings();

  const handleNavClick = (index) => {
    setSelected(index);
    navigate(getPath(index));
    if (index === 1) { // Messages tab
      dispatch(ResetMessageBadge());
    }
    if (index === 2) { // Pagers tab
      dispatch(ResetPagerBadge());
    }
  };

  const handleGearClick = () => {
    // Reverted to original behavior: just navigate to settings
    setSelected(4); // Set selected to settings index (shifted from 5 to 4)
    navigate(getPath(4)); // Navigate to settings page (shifted from 5 to 4)
  };

  // Updated Profile_Menu to only include Profile and Logout
  const Profile_Menu = [
    {
      title: "Profile",
      icon: <User />,
    },
    {
      title: "Logout",
      icon: <SignOut />,
    },
  ];

  return (
    <Box p={2} sx={{ backgroundColor: theme.palette.background.paper, 
        boxShadow: "0px 0px 2px rgba(0,0,0,0.25)", height: "100vh", width: 100, display:"flex" }}>
        <Stack direction="column" alignItems={"center"} justifyContent="space-between" 
        sx={{ width: "100%" , height: "100%"}} spacing={3}>
          <Stack alignItems={"center"} spacing={4}>
          <Box sx={{
            // Removed backgroundColor: theme.palette.primary.main,
            height: 64,
            width: 64,
            borderRadius: 1.5
          }}>
            <img src={LogoTop} alt={'Logo icon'} /> {/* Changed to LogoTop */}
          </Box>
          <Stack sx={{ width: "max-content" }} direction="column" alignItems="center" spacing={3}>
            {Nav_Buttons.map((el) => {
              const badgeCount = el.index === 1 ? messageBadgeCount : el.index === 2 ? pagerBadgeCount : 0;
              return (
                el.index === selected ?
                  <Tooltip title={el.title} placement="right" key={el.index}>
                  <Box sx={{ backgroundColor: theme.palette.primary.main, borderRadius: 1.5 }}>
                    <IconButton sx={{ width: "max-content", color: "#fff" }}>
                      <Badge badgeContent={badgeCount} color="error">
                        {el.icon}
                      </Badge>
                    </IconButton>
                  </Box>
                  </Tooltip>
                  :
                  <Tooltip title={el.title} placement="right" key={el.index}>
                  <IconButton onClick={() => handleNavClick(el.index)} 
                  sx={{ width: "max-content", color:theme.palette.mode === 'light' ? "#000" 
                  : theme.palette.text.primary }}>
                    <Badge badgeContent={badgeCount} color="error">
                      {el.icon}
                    </Badge>
                  </IconButton>
                  </Tooltip>
              )
            })}
            <Divider sx={{ width: "48px" }} />
            {selected === 4 ? // Settings index, shifted from 5 to 4
              <Tooltip title="More" placement="right">
              <Box sx={{ backgroundColor: theme.palette.primary.main, borderRadius: 1.5 }}>
                <IconButton onClick={handleGearClick} sx={{ width: "max-content", color: "#fff" }}>
                  <Gear />
                </IconButton>
              </Box>
              </Tooltip>
              :
              <Tooltip title="More" placement="right">
              <IconButton onClick={handleGearClick} sx={{ width: "max-content",
               color: theme.palette.mode === 'light' ? "#000" :theme.palette.text.primary }} >
                <Gear />
              </IconButton>
              </Tooltip>
            }

          </Stack>

          </Stack>
          
          <Stack spacing={4}>
            <Tooltip title="Switch Theme" placement="right">
              <div> {/* Tooltip needs a child ref, AntSwitch might not forward it correctly depending on implementation, wrapping in div or span helps */}
                <AntSwitch onChange={()=>{
                    onToggleMode();
                }} defaultChecked/>
              </div>
            </Tooltip>
            
            <Tooltip title="Profile & Logout" placement="right">
              <Avatar 
                id='basic-button' 
                sx={{
                  cursor:'pointer',
                  bgcolor: !avatarSrc ? avatarProps.color : undefined,
                  color: !avatarSrc ? theme.palette.getContrastText(avatarProps.color) : undefined
                }}
                src={avatarSrc}
                alt={userName}
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
              >
                {!avatarSrc && avatarProps.name}
              </Avatar>
            </Tooltip>

            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
              anchorOrigin={{vertical:'bottom', horizontal:'right'}}
              transformOrigin={{vertical:'bottom', horizontal:'left'}}
            >
            <Stack spacing={1} px={1}>
              {Profile_Menu.map((el, idx)=>(
                  <MenuItem key={el.title} onClick={ ()=> {handleClose(); } }> {/* Added key prop */}
                    <Stack onClick={()=>{
                      navigate(getMenuPath(idx))
                    }} sx={{width:180}} direction='row' alignItems={'center'}
                     justifyContent='start' spacing={1}> {/* Changed width, direction, justifyContent, and added spacing */}
                      {el.icon} {/* Moved icon to the left */}
                      <span>{el.title}</span>
                    </Stack>  
                  </MenuItem>
              ))}
            </Stack>
          </Menu>
          </Stack>   
        </Stack>
        
      </Box>
  );
};

export default SideBar;